import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { DataTable, type DataTableColumn } from '@/components/data-display/data-table'
import { ErrorState } from '@/components/feedback/error-state'
import { useFeedback } from '@/components/feedback/feedback-context'
import { FormField } from '@/components/forms/form-field'
import { Input } from '@/components/forms/input'
import { PasswordInput } from '@/components/forms/password-input'
import { PageHeader } from '@/components/layout/page-header'
import { ActionMenu } from '@/components/overlays/action-menu'
import { HelpTooltip } from '@/components/overlays/help-tooltip'
import { Modal } from '@/components/overlays/modal'
import { Button } from '@/components/primitives/button'
import actionDeleteIconUrl from '@/assets/action-menu/delete.svg'
import actionEditIconUrl from '@/assets/action-menu/edit.svg'
import {
  createTencentAdpCredential,
  deleteTencentAdpCredential,
  listTencentAdpCredentials,
  tencentAdpCredentialOptionsQueryKey,
  tencentAdpCredentialsQueryKey,
  updateTencentAdpCredential,
  validateTencentAdpCredentialDraft,
  validateTencentAdpCredentialUpdate,
  type TencentAdpCredential,
  type TencentAdpCredentialCreatePayload,
  type TencentAdpCredentialUpdatePayload,
  type TencentAdpValidationResult,
} from '@/features/admin/adp-credentials/api'
import { formatDateTime } from '@/features/admin/shared/format'
import { AdminShellPage } from '@/features/admin/shell/shell-page'
import { ApiError } from '@/lib/api/http-client'

type CredentialDialogState =
  | {
      credential?: undefined
      mode: 'create'
    }
  | {
      credential: TencentAdpCredential
      mode: 'edit'
    }

interface CredentialFormState {
  name: string
  openapiEndpoint: string
  tencentRegion: string
  tencentSecretId: string
  tencentSecretKey: string
}

type CredentialFormErrors = Partial<Record<keyof CredentialFormState, string>>

const defaultTencentRegion = 'ap-guangzhou'
const defaultOpenapiEndpoint = 'https://adp.tencentcloudapi.com'
const tencentApiKeyConsoleUrl = 'https://console.cloud.tencent.com/cam/capi'

function formatApiError(error: unknown, fallback: string) {
  if (error instanceof ApiError && error.message.trim().length > 0) {
    return error.message
  }

  return fallback
}

function validationFailureDetail(result: TencentAdpValidationResult) {
  return result.stages.find((stage) => stage.status === 'failed')?.message ?? result.message
}

function validationSuccessDetail(t: ReturnType<typeof useTranslation<'adminSettings'>>['t']) {
  return t('adpCredentials.feedback.validationPassedDetail')
}

function readCredentialFormState(credential?: TencentAdpCredential): CredentialFormState {
  return {
    name: credential?.name ?? '',
    openapiEndpoint: credential?.openapi_endpoint ?? defaultOpenapiEndpoint,
    tencentRegion: credential?.tencent_region ?? defaultTencentRegion,
    tencentSecretId: credential?.tencent_secret_id ?? '',
    tencentSecretKey: '',
  }
}

function normalizeEndpoint(value: string) {
  return value.trim().replace(/\/+$/, '')
}

function isValidHttpEndpoint(value: string) {
  const endpoint = normalizeEndpoint(value)
  if (!endpoint.startsWith('http://') && !endpoint.startsWith('https://')) {
    return false
  }

  try {
    const url = new URL(endpoint)
    return Boolean(url.hostname)
  } catch {
    return false
  }
}

function buildCreatePayload(state: CredentialFormState): TencentAdpCredentialCreatePayload {
  return {
    name: state.name.trim(),
    openapi_endpoint: normalizeEndpoint(state.openapiEndpoint),
    tencent_region: state.tencentRegion.trim(),
    tencent_secret_id: state.tencentSecretId.trim(),
    tencent_secret_key: state.tencentSecretKey.trim(),
  }
}

function buildUpdatePayload(state: CredentialFormState): TencentAdpCredentialUpdatePayload {
  const secretId = state.tencentSecretId.trim()
  const secretKey = state.tencentSecretKey.trim()

  return {
    name: state.name.trim(),
    openapi_endpoint: normalizeEndpoint(state.openapiEndpoint),
    tencent_region: state.tencentRegion.trim(),
    tencent_secret_id: secretId,
    tencent_secret_key: secretKey.length > 0 ? secretKey : undefined,
  }
}

export function AdminAdpCredentialsPage() {
  const { t } = useTranslation('adminSettings')
  const { showFeedback } = useFeedback()
  const queryClient = useQueryClient()
  const [dialogState, setDialogState] = useState<CredentialDialogState | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<TencentAdpCredential | null>(null)

  const credentialsQuery = useQuery({
    queryFn: listTencentAdpCredentials,
    queryKey: tencentAdpCredentialsQueryKey,
  })

  const invalidateCredentialQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: tencentAdpCredentialsQueryKey }),
      queryClient.invalidateQueries({ queryKey: tencentAdpCredentialOptionsQueryKey }),
    ])
  }

  const createMutation = useMutation({
    mutationFn: createTencentAdpCredential,
    onError: (error) => {
      showFeedback({
        detail: formatApiError(error, t('errors.submitFailed')),
        message: t('adpCredentials.feedback.createFailed'),
        tone: 'error',
      })
    },
    onSuccess: async () => {
      setDialogState(null)
      await invalidateCredentialQueries()
      showFeedback({
        message: t('success.createAdpCredential'),
        tone: 'success',
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: updateTencentAdpCredential,
    onError: (error) => {
      showFeedback({
        detail: formatApiError(error, t('errors.submitFailed')),
        message: t('adpCredentials.feedback.updateFailed'),
        tone: 'error',
      })
    },
    onSuccess: async () => {
      setDialogState(null)
      await invalidateCredentialQueries()
      showFeedback({
        message: t('success.updateAdpCredential'),
        tone: 'success',
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: deleteTencentAdpCredential,
    onError: (error) => {
      showFeedback({
        detail: formatApiError(error, t('adpCredentials.deleteDialog.failed')),
        message: t('adpCredentials.deleteDialog.failed'),
        tone: 'error',
      })
    },
    onSuccess: async () => {
      setDeleteTarget(null)
      await invalidateCredentialQueries()
      showFeedback({
        message: t('success.deleteAdpCredential'),
        tone: 'success',
      })
    },
  })

  const rows = credentialsQuery.data ?? []
  const columns = useMemo<DataTableColumn<TencentAdpCredential>[]>(
    () => [
      {
        header: t('adpCredentials.table.name'),
        id: 'name',
        render: (credential) => (
          <div className="grid min-w-0 gap-1">
            <span className="truncate font-medium">{credential.name}</span>
          </div>
        ),
        width: 240,
      },
      {
        header: t('adpCredentials.table.secretId'),
        id: 'secret-id',
        render: (credential) => (
          <span className="block max-w-[260px] truncate" title={credential.tencent_secret_id}>
            {credential.tencent_secret_id || '-'}
          </span>
        ),
        width: 280,
      },
      {
        header: t('adpCredentials.table.region'),
        id: 'region',
        render: (credential) => credential.tencent_region,
        width: 150,
      },
      {
        header: t('adpCredentials.table.endpoint'),
        id: 'endpoint',
        render: (credential) => (
          <span className="block max-w-[280px] truncate" title={credential.openapi_endpoint}>
            {credential.openapi_endpoint}
          </span>
        ),
        width: 300,
      },
      {
        header: t('adpCredentials.table.updatedAt'),
        id: 'updated-at',
        render: (credential) => formatDateTime(credential.updated_at),
        width: 180,
      },
      {
        align: 'center',
        header: t('adpCredentials.table.actions'),
        id: 'actions',
        pin: 'right',
        render: (credential) => (
          <ActionMenu
            ariaLabel={credential.name}
            items={[
              {
                iconSrc: actionEditIconUrl,
                key: 'edit',
                label: t('actions.edit'),
                onSelect: () => setDialogState({ credential, mode: 'edit' }),
              },
              {
                iconSrc: actionDeleteIconUrl,
                key: 'delete',
                label: t('actions.delete'),
                onSelect: () => setDeleteTarget(credential),
                variant: 'danger',
              },
            ]}
          />
        ),
        width: 90,
      },
    ],
    [t],
  )

  return (
    <AdminShellPage activePath="/admin/credentials/adp">
      <section
        aria-label={t('adpCredentials.workspaceLabel')}
        className="flex h-full min-w-0 flex-col overflow-hidden bg-white"
        data-testid="admin-adp-credentials-workspace"
      >
        <PageHeader
          actions={
            <Button onClick={() => setDialogState({ mode: 'create' })} size="sm">
              <PlusIcon />
              {t('actions.createCredential')}
            </Button>
          }
          description={t('adpCredentials.description')}
          title={t('adpCredentials.title')}
        />
        <div className="min-h-0 flex-1 overflow-auto px-5 py-5 lg:px-[30px]">
          {credentialsQuery.isError ? (
            <ErrorState
              action={
                <Button
                  className="mt-3"
                  onClick={() => {
                    void credentialsQuery.refetch()
                  }}
                  size="sm"
                  variant="secondary"
                >
                  {t('actions.retry')}
                </Button>
              }
              description={t('adpCredentials.loadDescription')}
              title={t('adpCredentials.loadTitle')}
            />
          ) : (
            <DataTable
              columns={columns}
              empty={t('adpCredentials.empty')}
              getRowKey={(credential) => credential.id}
              isLoading={credentialsQuery.isLoading}
              rows={rows}
            />
          )}
        </div>
        {dialogState ? (
          <AdpCredentialFormModal
            credential={dialogState.credential}
            isSubmitting={createMutation.isPending || updateMutation.isPending}
            key={dialogState.mode === 'edit' ? dialogState.credential.id : 'create'}
            onClose={() => {
              if (!createMutation.isPending && !updateMutation.isPending) {
                setDialogState(null)
              }
            }}
            onCreate={(payload) => createMutation.mutate(payload)}
            onUpdate={(credentialId, payload) =>
              updateMutation.mutate({ credentialId, values: payload })
            }
            open={Boolean(dialogState)}
          />
        ) : null}
        <DeleteAdpCredentialDialog
          credential={deleteTarget}
          isSubmitting={deleteMutation.isPending}
          onClose={() => {
            if (!deleteMutation.isPending) {
              setDeleteTarget(null)
            }
          }}
          onConfirm={(credentialId) => deleteMutation.mutate(credentialId)}
        />
      </section>
    </AdminShellPage>
  )
}

function AdpCredentialFormModal({
  credential,
  isSubmitting,
  onClose,
  onCreate,
  onUpdate,
  open,
}: {
  credential?: TencentAdpCredential
  isSubmitting: boolean
  onClose: () => void
  onCreate: (payload: TencentAdpCredentialCreatePayload) => void
  onUpdate: (credentialId: string, payload: TencentAdpCredentialUpdatePayload) => void
  open: boolean
}) {
  const { t } = useTranslation('adminSettings')
  const { showFeedback } = useFeedback()
  const isEdit = Boolean(credential)
  const [formState, setFormState] = useState<CredentialFormState>(() =>
    readCredentialFormState(credential),
  )
  const [errors, setErrors] = useState<CredentialFormErrors>({})
  const [validationIntent, setValidationIntent] = useState<'manual' | 'submit' | null>(null)

  const validateMutation = useMutation({
    mutationFn: async () => {
      if (credential) {
        return validateTencentAdpCredentialUpdate({
          credentialId: credential.id,
          values: buildUpdatePayload(formState),
        })
      }

      return validateTencentAdpCredentialDraft(buildCreatePayload(formState))
    },
  })

  const updateValue = <Key extends keyof CredentialFormState>(
    key: Key,
    value: CredentialFormState[Key],
  ) => {
    setFormState((current) => ({ ...current, [key]: value }))
    setErrors((current) => ({ ...current, [key]: undefined }))
  }

  const validate = () => {
    const nextErrors: CredentialFormErrors = {}

    if (!formState.name.trim()) {
      nextErrors.name = t('adpCredentials.validation.nameRequired')
    }
    if (!formState.tencentSecretId.trim()) {
      nextErrors.tencentSecretId = t('adpCredentials.validation.secretIdRequired')
    }
    if (!isEdit && !formState.tencentSecretKey.trim()) {
      nextErrors.tencentSecretKey = t('adpCredentials.validation.secretKeyRequired')
    }
    if (!formState.tencentRegion.trim()) {
      nextErrors.tencentRegion = t('adpCredentials.validation.regionRequired')
    }
    if (!formState.openapiEndpoint.trim()) {
      nextErrors.openapiEndpoint = t('adpCredentials.validation.endpointRequired')
    } else if (!isValidHttpEndpoint(formState.openapiEndpoint)) {
      nextErrors.openapiEndpoint = t('adpCredentials.validation.endpointInvalid')
    }

    return nextErrors
  }

  const focusFirstError = (nextErrors: CredentialFormErrors) => {
    const firstErrorKey = Object.keys(nextErrors)[0]
    if (!firstErrorKey) {
      return
    }

    window.requestAnimationFrame(() => {
      document.getElementById(`adp-credential-${firstErrorKey}`)?.focus()
    })
  }

  const runConnectivityTest = async (intent: 'manual' | 'submit') => {
    const nextErrors = validate()
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      focusFirstError(nextErrors)
      return false
    }

    setErrors({})
    setValidationIntent(intent)
    try {
      const result = await validateMutation.mutateAsync()
      if (!result.ok) {
        showFeedback({
          detail: validationFailureDetail(result),
          message: t('adpCredentials.feedback.validationFailed'),
          tone: 'error',
        })
        return false
      }
      if (intent === 'manual') {
        showFeedback({
          detail: validationSuccessDetail(t),
          message: t('adpCredentials.feedback.validationPassed'),
          tone: 'success',
        })
      }
      return true
    } catch (error) {
      showFeedback({
        detail: formatApiError(error, t('adpCredentials.feedback.validationFailed')),
        message: t('adpCredentials.feedback.validationFailed'),
        tone: 'error',
      })
      return false
    } finally {
      setValidationIntent(null)
    }
  }

  const handleTestCredential = () => {
    void runConnectivityTest('manual')
  }

  const handleSubmit = async () => {
    const isConnected = await runConnectivityTest('submit')
    if (!isConnected) {
      return
    }

    if (credential) {
      onUpdate(credential.id, buildUpdatePayload(formState))
      return
    }

    onCreate(buildCreatePayload(formState))
  }

  const isBusy = isSubmitting || validationIntent !== null

  return (
    <Modal
      className="max-w-[560px]"
      closeLabel={t('adpCredentials.form.close')}
      footer={
        <>
          <Button
            className="mr-auto"
            disabled={isBusy}
            isLoading={validationIntent === 'manual'}
            onClick={handleTestCredential}
            size="sm"
            variant="soft"
          >
            {t('adpCredentials.form.testCredential')}
          </Button>
          <Button disabled={isBusy} onClick={onClose} size="sm" variant="secondary">
            {t('actions.cancel')}
          </Button>
          <Button
            isLoading={isSubmitting || validationIntent === 'submit'}
            onClick={handleSubmit}
            size="sm"
          >
            {isEdit ? t('adpCredentials.form.update') : t('adpCredentials.form.create')}
          </Button>
        </>
      }
      onClose={() => {
        if (!isBusy) {
          onClose()
        }
      }}
      open={open}
      subtitle={isEdit ? undefined : t('adpCredentials.form.createSubtitle')}
      title={
        isEdit
          ? t('adpCredentials.form.editTitle', { name: credential?.name ?? '' })
          : t('adpCredentials.form.createTitle')
      }
    >
      <div className="grid gap-4">
        <CredentialField
          error={errors.name}
          htmlFor="adp-credential-name"
          label={t('adpCredentials.form.fields.name')}
          required
        >
          <Input
            id="adp-credential-name"
            invalid={Boolean(errors.name)}
            onChange={(event) => updateValue('name', event.target.value)}
            placeholder={t('adpCredentials.form.placeholders.name')}
            value={formState.name}
          />
        </CredentialField>
        <CredentialField
          error={errors.tencentSecretId}
          htmlFor="adp-credential-tencentSecretId"
          labelExtra={<TencentApiKeyGuide />}
          label={t('adpCredentials.form.fields.secretId')}
          required
        >
          <Input
            id="adp-credential-tencentSecretId"
            invalid={Boolean(errors.tencentSecretId)}
            onChange={(event) => updateValue('tencentSecretId', event.target.value)}
            placeholder={t('adpCredentials.form.placeholders.secretId')}
            value={formState.tencentSecretId}
          />
        </CredentialField>
        <CredentialField
          error={errors.tencentSecretKey}
          hint={
            isEdit
              ? t('adpCredentials.form.secretPreserveHint', {
                  value: credential?.masked_tencent_secret_key ?? '',
                })
              : undefined
          }
          htmlFor="adp-credential-tencentSecretKey"
          label={t('adpCredentials.form.fields.secretKey')}
          required={!isEdit}
        >
          <PasswordInput
            hideLabel={t('adpCredentials.form.hideSecret')}
            id="adp-credential-tencentSecretKey"
            invalid={Boolean(errors.tencentSecretKey)}
            onChange={(event) => updateValue('tencentSecretKey', event.target.value)}
            placeholder={
              isEdit
                ? t('adpCredentials.form.placeholders.secretKeyEdit')
                : t('adpCredentials.form.placeholders.secretKey')
            }
            showLabel={t('adpCredentials.form.showSecret')}
            value={formState.tencentSecretKey}
          />
        </CredentialField>
        <div className="grid gap-4">
          <CredentialField
            error={errors.tencentRegion}
            htmlFor="adp-credential-tencentRegion"
            label={t('adpCredentials.form.fields.region')}
            required
          >
            <Input
              id="adp-credential-tencentRegion"
              invalid={Boolean(errors.tencentRegion)}
              onChange={(event) => updateValue('tencentRegion', event.target.value)}
              placeholder={defaultTencentRegion}
              value={formState.tencentRegion}
            />
          </CredentialField>
        </div>
        <CredentialField
          error={errors.openapiEndpoint}
          htmlFor="adp-credential-openapiEndpoint"
          label={t('adpCredentials.form.fields.openapiEndpoint')}
          required
        >
          <Input
            id="adp-credential-openapiEndpoint"
            invalid={Boolean(errors.openapiEndpoint)}
            onChange={(event) => updateValue('openapiEndpoint', event.target.value)}
            placeholder={defaultOpenapiEndpoint}
            value={formState.openapiEndpoint}
          />
        </CredentialField>
      </div>
    </Modal>
  )
}

function CredentialField({
  children,
  error,
  hint,
  htmlFor,
  label,
  labelExtra,
  required = false,
}: {
  children: ReactNode
  error?: string
  hint?: string
  htmlFor: string
  label: string
  labelExtra?: ReactNode
  required?: boolean
}) {
  return (
    <FormField
      error={error}
      hint={hint}
      htmlFor={htmlFor}
      label={label}
      labelExtra={labelExtra}
      required={required}
    >
      {children}
    </FormField>
  )
}

function TencentApiKeyGuide() {
  const { t } = useTranslation('adminSettings')
  const tooltip = t('adpCredentials.form.secretGuide.tooltip')

  return (
    <span className="inline-flex items-center gap-1.5">
      <HelpTooltip label={tooltip} widthClassName="w-[280px]">
        {tooltip}
      </HelpTooltip>
      <a
        className="text-xs font-medium text-[var(--color-primary)] hover:text-[var(--color-primary-hover)]"
        href={tencentApiKeyConsoleUrl}
        rel="noreferrer"
        target="_blank"
      >
        {t('adpCredentials.form.secretGuide.link')}
      </a>
    </span>
  )
}

function DeleteAdpCredentialDialog({
  credential,
  isSubmitting,
  onClose,
  onConfirm,
}: {
  credential: TencentAdpCredential | null
  isSubmitting: boolean
  onClose: () => void
  onConfirm: (credentialId: string) => void
}) {
  const { t } = useTranslation('adminSettings')

  return (
    <Modal
      closeLabel={t('adpCredentials.deleteDialog.close')}
      footer={
        <>
          <Button disabled={isSubmitting} onClick={onClose} size="sm" variant="secondary">
            {t('actions.cancel')}
          </Button>
          <Button
            disabled={!credential}
            isLoading={isSubmitting}
            onClick={() => {
              if (credential) {
                onConfirm(credential.id)
              }
            }}
            size="sm"
            variant="danger"
          >
            {t('adpCredentials.deleteDialog.confirm')}
          </Button>
        </>
      }
      onClose={onClose}
      open={Boolean(credential)}
      title={t('adpCredentials.deleteDialog.title')}
    >
      <p className="text-sm leading-6 text-[var(--color-text)]">
        {t('adpCredentials.deleteDialog.message', { name: credential?.name ?? '' })}
      </p>
    </Modal>
  )
}

function PlusIcon() {
  return (
    <svg aria-hidden className="size-4" fill="none" viewBox="0 0 16 16">
      <path d="M8 3.5v9M3.5 8h9" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
    </svg>
  )
}
